import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
	Card,
	CardBody,
	Badge,
	Button,
	Modal,
	ModalHeader,
	ModalBody,
	Input,
} from 'reactstrap';
import { ODataGrid } from '../../../Components/OElements/ODataGrid';
import {
	getWarehouseGuides,
	cancelWarehouseGuide,
} from '../../../slices/warehouseGuide/thunk';
import {
	cancelWarehouseSendFromStock,
	cancelGuideLinesSend,
} from '../../../slices/guide/thunk';
import { downloadFile } from '../../../slices/thunks';
import { toast } from 'react-toastify';
import PdfPreviewModal from '../../../Components/General/Modal/PdfPreviewModal';

// ─── Mapeos de categoría y operación ────────────────────────────────
const CATEGORY_CONFIG = {
	epp: { label: 'EPP', color: 'primary', icon: 'ri-shield-user-line' },
	residuos: { label: 'Residuos', color: 'success', icon: 'ri-recycle-line' },
	general: {
		label: 'General',
		color: 'secondary',
		icon: 'ri-file-list-3-line',
	},
};

const OPERATION_LABELS = {
	// EPP
	retiro: 'Retiro',
	transferencia: 'Transferencia',
	// Residuos (alineado con `workflows/residuos/index.js` → `config.operationTypes`)
	peligrosos: 'Peligrosos',
	no_peligrosos: 'No Peligrosos',
	limpieza_playa: 'Limpieza de Playa',
	envio_bodega_peligrosos: 'Envío desde bodega — Peligrosos',
	envio_bodega_no_peligrosos: 'Envío desde bodega — No peligrosos',
};

// Tipos de operación agrupados por categoría (para filtro dinámico → query `operation_type` al API)
const OPERATIONS_BY_CATEGORY = {
	epp: [
		{ value: 'retiro', label: 'Retiro' },
		{ value: 'transferencia', label: 'Transferencia' },
	],
	residuos: [
		{ value: 'peligrosos', label: 'Peligrosos' },
		{ value: 'no_peligrosos', label: 'No Peligrosos' },
		{ value: 'limpieza_playa', label: 'Limpieza de Playa' },
		{ value: 'envio_bodega_peligrosos', label: 'Envío desde bodega — Peligrosos' },
		{ value: 'envio_bodega_no_peligrosos', label: 'Envío desde bodega — No peligrosos' },
	],
};

const GuidesList = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { folio } = useParams(); // Capturar folio de la URL /warehouse/guides/:folio
	const { guides, isLoading } = useSelector(state => state.WarehouseGuide);
	const [filters, setFilters] = useState({
		guide_category: 'all',
		operation_type: 'all',
		cancelled: null,
	});
	const [autoOpenFolio, setAutoOpenFolio] = useState(null); // Folio a abrir automáticamente
	const [cancelModal, setCancelModal] = useState({
		open: false,
		guideId: null,
		residueGuideId: null,
		operationType: null,
	});
	const [cancelReason, setCancelReason] = useState('');
	const [detailModal, setDetailModal] = useState({
		open: false,
		guide: null,
	});
	const [pdfPreview, setPdfPreview] = useState({
		open: false,
		blob: null,
		title: '',
	});
	const [pdfLoading, setPdfLoading] = useState(null);

	// Opciones de tipo de operación dinámicas según categoría seleccionada
	const operationTypeOptions = useMemo(() => {
		if (filters.guide_category === 'all') {
			// Mostrar todas las operaciones de todas las categorías
			return Object.entries(OPERATIONS_BY_CATEGORY).flatMap(
				([, ops]) => ops,
			);
		}
		return OPERATIONS_BY_CATEGORY[filters.guide_category] || [];
	}, [filters.guide_category]);

	// Efecto para manejar folio en URL - abrir PDF automáticamente
	useEffect(() => {
		if (folio) {
			// Guardar el folio para abrir cuando las guías carguen
			setAutoOpenFolio(folio);
			// NOTA: La URL mantiene el folio visible /warehouse/guides/{folio}
		}
	}, [folio]);

	// Efecto para abrir PDF automáticamente cuando se carga la guía
	useEffect(() => {
		if (autoOpenFolio && guides?.length > 0) {
			// Buscar la guía por folio
			const targetGuide = guides.find(
				g => String(g.folio) === String(autoOpenFolio)
			);
			
			if (targetGuide) {
				// Abrir el PDF automáticamente
				handleClickPdf(targetGuide);
				setAutoOpenFolio(null); // Limpiar para no volver a abrir
			} else {
				// Guía no encontrada - mostrar error
				toast.error(`Guía #${autoOpenFolio} no encontrada en el sistema`);
				setAutoOpenFolio(null);
			}
		}
	}, [guides, autoOpenFolio]);

	useEffect(() => {
		const queryParams = {};
		if (filters.guide_category !== 'all')
			queryParams.guide_category = filters.guide_category;
		if (filters.operation_type !== 'all')
			queryParams.operation_type = filters.operation_type;
		if (filters.cancelled !== null) queryParams.cancelled = filters.cancelled;

		dispatch(getWarehouseGuides(queryParams));
	}, [filters, dispatch]);

	const WAREHOUSE_SEND_OPS = new Set([
		'envio_bodega_peligrosos',
		'envio_bodega_no_peligrosos',
	]);

	const handleCancelGuide = async () => {
		if (!cancelReason.trim()) {
			toast.error('Debe ingresar una razón para anular');
			return;
		}

		try {
			const isStockSend =
				cancelModal.operationType &&
				WAREHOUSE_SEND_OPS.has(cancelModal.operationType);

			if (isStockSend && cancelModal.residueGuideId) {
				if (
					cancelModal.operationType === 'envio_bodega_no_peligrosos'
				) {
					await dispatch(
						cancelGuideLinesSend({
							guideId: cancelModal.residueGuideId,
							mode: 'manual',
							reason: cancelReason.trim(),
						}),
					).unwrap();
				} else {
					await dispatch(
						cancelWarehouseSendFromStock({
							guideId: cancelModal.residueGuideId,
							mode: 'manual',
							reason: cancelReason.trim(),
						}),
					).unwrap();
				}
			} else if (isStockSend && !cancelModal.residueGuideId) {
				toast.warning(
					'No se encontró guía de residuos vinculada; solo se anulará el listado unificado.',
					{ autoClose: 5000 },
				);
			}

			await dispatch(
				cancelWarehouseGuide({
					guideId: cancelModal.guideId,
					reason: cancelReason,
				}),
			).unwrap();

			setCancelModal({
				open: false,
				guideId: null,
				residueGuideId: null,
				operationType: null,
			});
			setCancelReason('');

			// Recargar lista con los mismos filtros
			const queryParams = {};
			if (filters.guide_category !== 'all')
				queryParams.guide_category = filters.guide_category;
			if (filters.operation_type !== 'all')
				queryParams.operation_type = filters.operation_type;
			if (filters.cancelled !== null)
				queryParams.cancelled = filters.cancelled;

			dispatch(getWarehouseGuides(queryParams));
		} catch (error) {
			// Error ya manejado en thunk
		}
	};

	const handleClickDelete = useCallback(guide => {
		setCancelModal({
			open: true,
			guideId: guide._id,
			residueGuideId: guide.residue_guide_id || null,
			operationType: guide.operation_type || null,
		});
	}, []);

	const handleClickView = useCallback(guide => {
		viewGuide(guide._id);
	}, []);

	const handleClickPdf = useCallback(
		async guide => {
			if (!guide.document_id) {
				toast.error('Esta guía no tiene documento PDF asociado');
				return;
			}

			try {
				setPdfLoading(guide._id);
				const result = await dispatch(
					downloadFile({ file_id: guide.document_id, disable_msg: true }),
				);
				const blob = result.payload;

				if (!(blob instanceof Blob)) {
					throw new Error('No se pudo obtener el archivo');
				}

				setPdfPreview({
					open: true,
					blob: blob,
					title: `Guía de Despacho #${guide.folio}`,
				});
			} catch (error) {
				console.error('Error al obtener el PDF:', error);
				toast.error(`Error al cargar el PDF de la guía #${guide.folio}`);
			} finally {
				setPdfLoading(null);
			}
		},
		[dispatch],
	);

	const closePdfPreview = useCallback(() => {
		setPdfPreview({ open: false, blob: null, title: '' });
	}, []);

	// ─── Helper para renderizar badge de categoría ─────────────────
	const renderCategoryBadge = useCallback(category => {
		const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.general;
		return (
			<Badge color={config.color}>
				<i className={`${config.icon} me-1`}></i>
				{config.label}
			</Badge>
		);
	}, []);

	// ─── Helper para renderizar tipo de operación ──────────────────
	const renderOperationType = useCallback(operationType => {
		return OPERATION_LABELS[operationType] || operationType || '-';
	}, []);

	const columns = useMemo(
		() => [
			{
				header: 'Acciones',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				accessorKey: 'actions',
				columnDefType: 'display',
				size: 'auto',
				Cell: cellProps => {
					const guide = cellProps.row.original;
					const hasDocument = !!guide.document_id;
					const isCancelled = !!guide.cancelled;

					return (
						<div className='d-flex justify-content-center'>
							<div className='flex-shrink-0'>
												<ul className='list-inline mb-0'>
														<li className='list-inline-item'>
															<Link
																to='#'
																onClick={e => {
																	e.preventDefault();
																	if (hasDocument) handleClickPdf(guide);
																}}
																title={
																	hasDocument
																		? 'Ver PDF'
																		: 'Sin documento PDF'
																}
																style={{
																	opacity: hasDocument ? 1 : 0.3,
																	cursor: hasDocument
																		? 'pointer'
																		: 'not-allowed',
																}}
															>
																{pdfLoading === guide._id ? (
																	<div
																		className='spinner-border spinner-border-sm'
																		role='status'
																		style={{ width: '14px', height: '14px' }}
																	>
																		<span className='visually-hidden'>
																			Cargando...
																		</span>
																	</div>
																) : (
																	<i className='ri-file-pdf-line align-bottom me-2 text-muted'></i>
																)}
															</Link>
														</li>
									<li className='list-inline-item'>
										<Link
											to='#'
											className='remove-item-btn'
											onClick={e => {
												e.preventDefault();
												if (!isCancelled) handleClickDelete(guide);
											}}
											title={
												isCancelled
													? 'Guía ya anulada'
													: 'Anular guía'
											}
											style={{
												opacity: isCancelled ? 0.3 : 1,
												cursor: isCancelled
													? 'not-allowed'
													: 'pointer',
											}}
										>
											<i className='ri-close-circle-line align-bottom me-2 text-muted'></i>
										</Link>
									</li>
								</ul>
							</div>
						</div>
					);
				},
			},
		{
			header: 'Folio',
			accessorKey: 'folio',
			size: 'auto',
			Cell: cellProps => {
				const folio = cellProps.row.original.folio;
				return (
					<Link
						to={`/warehouse/guides/${folio}`}
						className='fw-bold text-primary'
						style={{ textDecoration: 'none', cursor: 'pointer' }}
						title={`Ver guía #${folio}`}
					>
						#{folio}
					</Link>
				);
			},
		},
			{
				header: 'Fecha',
				accessorKey: 'guide_date',
				dataType: 'date',
				size: 'auto',
			},
			{
				header: 'Categoría',
				accessorKey: 'guide_category',
				size: 'auto',
				Cell: cellProps =>
					renderCategoryBadge(cellProps.row.original.guide_category),
			},
			{
				header: 'Tipo de Operación',
				accessorKey: 'operation_type',
				size: 'auto',
				Cell: cellProps => {
					const guide = cellProps.row.original;
					const label = renderOperationType(guide.operation_type);
					// Color por categoría
					const catConfig = CATEGORY_CONFIG[guide.guide_category] || {};
					return (
						<span
							className={`text-${catConfig.color || 'dark'} fw-medium`}
						>
							{label}
						</span>
					);
				},
			},
			{
				header: 'Establecimiento',
				accessorKey: 'establishment.name',
				size: 'auto',
				Cell: cellProps =>
					cellProps.row.original.establishment?.name || '-',
			},
			{
				header: 'Estado',
				accessorKey: 'status_guide.name',
				size: 'auto',
				Cell: cellProps => {
					const guide = cellProps.row.original;
					const status = guide.status_guide;

					let statusText = 'N/A';
					let statusColor = 'secondary';

					if (guide.cancelled) {
						statusText = 'ANULADA';
						statusColor = 'danger';
					} else if (status?.name) {
						statusText = status.name.toUpperCase();
						const colors = {
							ACTIVA: 'success',
							ANULADA: 'danger',
							'PENDIENTE SAP': 'warning',
							'CONFIRMADA SAP': 'info',
							'ERROR SAP': 'danger',
						};
						statusColor = colors[statusText] || 'secondary';
					} else if (guide.status_guide_id) {
						const statusNames = {
							active: 'ACTIVA',
							cancelled: 'ANULADA',
							pending_sap: 'PENDIENTE SAP',
							sap_confirmed: 'CONFIRMADA SAP',
							error_sap: 'ERROR SAP',
						};
						statusText =
							statusNames[guide.status_guide_id] ||
							guide.status_guide_id.toUpperCase();

						const colors = {
							active: 'success',
							cancelled: 'danger',
							pending_sap: 'warning',
							sap_confirmed: 'info',
							error_sap: 'danger',
						};
						statusColor = colors[guide.status_guide_id] || 'secondary';
					}

					return <Badge color={statusColor}>{statusText}</Badge>;
				},
			},
			{
				header: 'Total',
				accessorKey: 'mnt_total',
				size: 'auto',
				Cell: cellProps => {
					const total = cellProps.row.original.mnt_total || 0;
					return `$${total.toLocaleString('es-CL')}`;
				},
			},
		],
		[
			handleClickDelete,
			handleClickPdf,
			pdfLoading,
			renderCategoryBadge,
			renderOperationType,
		],
	);

	// ─── Handler de cambio de categoría (resetea operación) ────────
	const handleCategoryChange = useCallback(e => {
		setFilters(prev => ({
			...prev,
			guide_category: e.target.value,
			operation_type: 'all', // Reset operación cuando cambia categoría
		}));
	}, []);

	const handleOperationChange = useCallback(e => {
		setFilters(prev => ({
			...prev,
			operation_type: e.target.value,
		}));
	}, []);

	const handleCancelledChange = useCallback(e => {
		const val = e.target.value;
		setFilters(prev => ({
			...prev,
			cancelled: val === 'all' ? null : val === 'true',
		}));
	}, []);

	return (
		<React.Fragment>
			<div className='page-content'>
				<Card>
					<CardBody>
						<div className='d-flex justify-content-between align-items-center mb-3'>
							<h4 className='mb-0'>Guías de Despacho</h4>
							<div className='d-flex gap-2'>
								{/* Stats rápidos por categoría */}
								{filters.guide_category === 'all' &&
									guides?.length > 0 && (
										<div className='d-flex gap-1 align-items-center'>
											{Object.entries(
												guides.reduce((acc, g) => {
													const cat =
														g.guide_category || 'general';
													acc[cat] = (acc[cat] || 0) + 1;
													return acc;
												}, {}),
											).map(([cat, count]) => {
												const config =
													CATEGORY_CONFIG[cat] ||
													CATEGORY_CONFIG.general;
												return (
													<Badge
														key={cat}
														color={config.color}
														className='px-2 py-1'
													>
														{config.label}: {count}
													</Badge>
												);
											})}
										</div>
									)}
							</div>
						</div>

						{/* Filtros */}
						<div className='mb-3 hstack gap-2 flex-wrap'>
							<select
								className='form-select'
								style={{ width: '200px' }}
								value={filters.guide_category}
								onChange={handleCategoryChange}
							>
								<option value='all'>Todas las categorías</option>
								<option value='epp'>EPP</option>
								<option value='residuos'>Residuos</option>
								<option value='general'>General</option>
							</select>

							<select
								className='form-select'
								style={{ width: '220px' }}
								value={filters.operation_type}
								onChange={handleOperationChange}
							>
								<option value='all'>Todas las operaciones</option>
								{operationTypeOptions.map(op => (
									<option key={op.value} value={op.value}>
										{op.label}
									</option>
								))}
							</select>

							<select
								className='form-select'
								style={{ width: '160px' }}
								value={filters.cancelled ?? 'all'}
								onChange={handleCancelledChange}
							>
								<option value='all'>Todas</option>
								<option value='false'>Activas</option>
								<option value='true'>Anuladas</option>
							</select>
						</div>

						<ODataGrid
							columns={columns}
							data={guides}
							pagination={true}
							isLoading={isLoading}
						/>
					</CardBody>
				</Card>

				{/* Modal de Anulación */}
				<Modal
					isOpen={cancelModal.open}
					toggle={() =>
						setCancelModal({
							open: false,
							guideId: null,
							residueGuideId: null,
							operationType: null,
						})
					}
				>
					<ModalHeader>Anular Guía de Despacho</ModalHeader>
					<ModalBody>
						<p>¿Está seguro que desea anular esta guía?</p>
						<p className='text-muted small'>
							Esta acción marcará la guía como anulada internamente. Si
							está confirmada en SAP, contacte al administrador.
						</p>

						<Input
							type='textarea'
							placeholder='Razón de anulación (requerido)'
							value={cancelReason}
							onChange={e => setCancelReason(e.target.value)}
							rows={3}
						/>

						<div className='mt-3 hstack gap-2 justify-content-end'>
							<Button
								color='secondary'
								onClick={() =>
									setCancelModal({
										open: false,
										guideId: null,
										residueGuideId: null,
										operationType: null,
									})
								}
							>
								Cancelar
							</Button>
							<Button color='danger' onClick={handleCancelGuide}>
								Anular Guía
							</Button>
						</div>
					</ModalBody>
				</Modal>

				{/* Modal de Preview PDF */}
				<PdfPreviewModal
					isOpen={pdfPreview.open}
					onClose={closePdfPreview}
					blob={pdfPreview.blob}
					title={pdfPreview.title}
				/>
			</div>
		</React.Fragment>
	);
};


export default GuidesList;
